import React from 'react';
import { motion } from 'framer-motion';
import { Target, Layers, TrendingUp, Users, Compass, ArrowRight } from 'lucide-react';
export function AboutPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-66" data-mp="16uycp3a7hqdq">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-67" data-mp="16x1ai9hlont0">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-68" data-mp="1225609v6g7z0">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-69" data-mp="1ptan0scut6aw">
            <span className="w-8 h-px bg-teal" data-id="element-70" data-mp="1kug4zsmpnlru"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-71" data-mp="1ksv8vykqzurv">
              Company
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-72" data-mp="3mmm4kkad4spo"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-73" data-mp="3mofcs40mjkzs">
            About SyNeural
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto mb-6" data-id="element-74" data-mp="3pt6jo2e29qu3">
            SyNeural is the parent platform behind the Civic Judgment Index
            (CJI) and the Neurocognitive Civic Judgment Index (NCJI) — a broader
            framework for human capability, civic development, institutional
            readiness, and leadership readiness.
          </p>

          <p className="text-lg text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-75" data-mp="d9x8gwr80p2z">
            The company was built around a simple premise: modern societies
            demand enormous amounts of judgment from individuals, institutions,
            and leaders, yet very few systems are designed to deliberately
            strengthen that judgment over time.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-76" data-mp="2o5440wa0y6qr">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-77" data-mp="3reqf15llpj05">
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-78" data-mp="3vdz8sagn5gvp">
            SyNeural exists to help address that gap. Through its platform
            architecture, the company is focused on building a more structured
            way to understand how people think, how they develop, how
            institutions can improve readiness, and how societies can create
            stronger pathways from potential to responsibility. Its work sits at
            the intersection of cognitive assessment, decision-quality
            analytics, opportunity guidance, civic resilience, and leadership
            qualification.
          </p>
        </motion.div>
      </section>

      {/* Our Mission */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-79" data-mp="1bl5136zh75ux">
        <div className="max-w-4xl mx-auto" data-id="element-80" data-mp="1gj6kjt0td76x">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-81" data-mp="11mfr00dtmpp8">
            <div className="flex items-center gap-4 mb-6" data-id="element-82" data-mp="xlm6zi3i1v1m">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-83" data-mp="iw6gm37vrrmg">
                <Target size={24} data-id="element-84" data-mp="nuhrmh9ww869" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-85" data-mp="nvap0rmzdlbq">
                Our Mission
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-86" data-mp="dwlfbm58n76k">
              SyNeural's mission is to strengthen human capability, improve
              civic judgment, expand opportunity, and support more resilient
              institutions by building systems that help people think more
              clearly, develop more effectively, and lead more responsibly. The
              company is focused not only on assessment, but on progression —
              turning insight into development, readiness, and long-term social
              value.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What We Build */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-87" data-mp="3f7vpwxvjeuko">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-88" data-mp="3abezty4s264y">
          <div className="flex items-center gap-4 mb-6" data-id="element-89" data-mp="13l7dobkhqhqw">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-90" data-mp="1c16d0a8cbceh">
              <Layers size={24} data-id="element-91" data-mp="1g29rasaqp0dl" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-92" data-mp="1g0ozhaft7vjf">
              What We Build
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed mb-8" data-id="element-93" data-mp="1psbg5max2mtk">
            At the center of the SyNeural ecosystem is CJI, the platform's
            analytical foundation for reasoning quality, decision competence,
            manipulation resistance, and leadership diagnostics. Built on that
            foundation is NCJI, a broader developmental and institutional
            architecture that expands into opportunity guidance, lifecycle
            development, public-sector readiness, and executive-level leadership
            qualification.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-94" data-mp="2dk04ysoj4t5r">
            Together, these systems form a unified platform for measuring,
            developing, and scaling judgment across the human and institutional
            spectrum.
          </p>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-95" data-mp="22r0v2h1kr9ks">
        <div className="max-w-4xl mx-auto" data-id="element-96" data-mp="22ooh0eyxtjd0">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-97" data-mp="1xrobc5602imf">
            <div className="flex items-center gap-4 mb-6" data-id="element-98" data-mp="1ju9gum0w3knl">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-99" data-mp="1f0f0nskhtnt9">
                <TrendingUp size={24} data-id="element-100" data-mp="rakn18scnv7y" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-101" data-mp="md0xhcq7bw0p">
                Why It Matters
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-102" data-mp="2xzcnnmkd596g">
              A society can have wealth, infrastructure, technology, and formal
              institutions, yet still weaken if too many decisions are shaped by
              poor reasoning, emotional reactivity, weak foresight, manipulation
              vulnerability, or degraded leadership. SyNeural was created from
              the belief that stronger institutions require a stronger human
              layer beneath them. That means better reasoning, better support
              structures, stronger readiness pathways, and more competent
              leadership over time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Vision */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-103" data-mp="1wase3qh4zmyd">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-104" data-mp="1wcvcaxsg0me6">
          <div className="flex items-center gap-4 mb-6" data-id="element-105" data-mp="2n3712nqrdyt9">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-106" data-mp="2j01yymalbz55">
              <Compass size={24} data-id="element-107" data-mp="2e1qny88k7ilc" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-108" data-mp="3aq6qd4wrpmfv">
              Our Vision
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-109" data-mp="36pwdd8ak5vri">
            SyNeural's long-term vision is to help create a new global
            infrastructure for human capability and civic development. The
            company sees a future in which societies are better equipped to
            cultivate judgment, strengthen institutions, align talent with
            opportunity, and prepare individuals for higher levels of
            responsibility across education, workforce systems, civil society,
            government, and leadership environments.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-110" data-mp="5rr8ypb1alxy">
            The goal is not simply to measure readiness, but to help build it.
          </p>
        </motion.div>
      </section>

      {/* Leadership */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-navy-deep text-white" data-id="element-111" data-mp="eu0ipcyhabaa">
        <div className="max-w-5xl mx-auto" data-id="element-112" data-mp="jt3ox0q72nju">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-113" data-mp="js22y17n4gnc">
            <div className="flex items-center gap-4 mb-8" data-id="element-114" data-mp="12jbml3b4klsn">
              <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center" data-id="element-115" data-mp="12gqj69v6hxss">
                <Users size={24} data-id="element-116" data-mp="17ftpryqt4c2v" />
              </div>
              <h2 className="text-3xl font-heading font-bold" data-id="element-117" data-mp="2a7hihj26w6fc">Leadership</h2>
            </div>

            <div className="bg-white/5 p-8 lg:p-10 rounded-xl border border-white/10" data-id="element-118" data-mp="2f4ztdezrovpz">
              <h3 className="text-2xl font-heading font-bold mb-2" data-id="element-119" data-mp="2k4cbjmfp1em8">
                Shawn Blake, CEO
              </h3>
              <p className="text-lg leading-relaxed opacity-90" data-id="element-120" data-mp="2ownn5mjsqoum">
                Shawn Blake leads SyNeural with a vision centered on human
                capability, civic resilience, and institutional strength. As
                CEO, he is focused on building SyNeural into a platform that can
                help individuals grow, institutions modernize, and leadership
                become more rigorous, more developmental, and more aligned with
                long-term societal stability. Under his leadership, SyNeural is
                positioned not just as a product company, but as the foundation
                for a broader architecture of human development and
                decision-quality at scale.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The SyNeural Approach */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-121" data-mp="3ly5m1ka0of8w">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-122" data-mp="3hyn3gv5o4g98">
          <div className="flex items-center gap-4 mb-8" data-id="element-123" data-mp="9gh6k7l892h8">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-124" data-mp="4ifonpzjf97c">
              <ArrowRight size={24} data-id="element-125" data-mp="3voqltb1ffpte" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-126" data-mp="2p9pwvpatk7wb">
              The SyNeural Approach
            </h2>
          </div>

          <p className="text-lg text-ink-muted leading-relaxed mb-10" data-id="element-127" data-mp="2k9ld9qem6x1v">
            SyNeural is built around a simple operating logic: assess, explain,
            recommend, support, reassess, and advance. That principle runs
            throughout the platform and reflects the company's broader
            philosophy.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-128" data-mp="2ct827mlvbp1s">
            <div className="bg-surface-muted p-6 rounded-lg border border-line" data-id="element-129" data-mp="2hovs6486cm8c">
              <h3 className="font-heading font-bold text-ink-dark mb-2" data-id="element-130" data-mp="2312beg7crdo6">
                Insight should lead to growth
              </h3>
              <p className="text-sm text-ink-muted" data-id="element-131" data-mp="27tc0ju0n89pe">
                Understanding capability gaps is only valuable when paired with
                development pathways
              </p>
            </div>

            <div className="bg-surface-muted p-6 rounded-lg border border-line" data-id="element-132" data-mp="1y04wcchn2zqw">
              <h3 className="font-heading font-bold text-ink-dark mb-2" data-id="element-133" data-mp="1k2yf4mckd93s">
                Gaps should be paired with pathways
              </h3>
              <p className="text-sm text-ink-muted" data-id="element-134" data-mp="15fraeydo97dc">
                Every identified weakness should connect to concrete support and
                progression routes
              </p>
            </div>

            <div className="bg-surface-muted p-6 rounded-lg border border-line" data-id="element-135" data-mp="1fdnp205tikg7">
              <h3 className="font-heading font-bold text-ink-dark mb-2" data-id="element-136" data-mp="1fbkq2qmotgzc">
                Outcomes need stronger systems
              </h3>
              <p className="text-sm text-ink-muted" data-id="element-137" data-mp="1odtztea4t6bo">
                Better results require better institutional and developmental
                infrastructure
              </p>
            </div>
          </div>

          <p className="text-lg text-ink-muted leading-relaxed mt-10" data-id="element-138" data-mp="1y33pknhi6cwm">
            This approach is what makes SyNeural more than a measurement
            platform — it makes it a framework for progression across the full
            spectrum of responsibility.
          </p>
        </motion.div>
      </section>
    </div>;
}